import {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {CircularProgress, Typography, Box, Tabs, Tab, TextField, IconButton, InputAdornment, Button, FormControlLabel, Switch} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Virtuoso} from 'react-virtuoso'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Editor from '@monaco-editor/react'
import {fetchS3File, JsonView} from './shared'

interface ExecutionTreeTabProps {
    record: any
}

function buildNodeMap(node: any, path: string, map: Map<string, any>): void {
    if (!node) return
    const nodeId = String(node.id)
    const uniqueId = `${path}/${nodeId}`
    map.set(uniqueId, node)
    if (node.children) {
        node.children.forEach((child: any, index: number) => {
            buildNodeMap(child, `${uniqueId}-${index}`, map)
        })
    }
}

function findFormulaById(formulas: any, id: string | number): {formula: any, domain: string, coverage: string} | null {
    if (!formulas) return null

    const targetId = String(id)
    for (const domain of Object.keys(formulas)) {
        const coverages = formulas[domain]
        if (typeof coverages !== 'object') continue

        for (const coverage of Object.keys(coverages)) {
            const formula = coverages[coverage]

            const formuals = Object.values(formula) as any[];
            for ( let i=0; i<formuals.length; i++ ) {
                const f = formuals[i];
                if (f && String(f?.RatingFormulaColumnID) === targetId) {
                    return {formula: f, domain, coverage}
                }
            }

        }
    }
    return null
}

function getCoverageDescription(coverageCode: string, commonTables: any): string {
    if (!commonTables?.Coverage || coverageCode === 'NoCoverage') return coverageCode
    const found = commonTables.Coverage.find((c: any) => c.DisplayCode === coverageCode)
    return found?.Description + ' (' + coverageCode + ')' || coverageCode
}

function getDomainDisplayName(domainInnerName: string, commonTables: any): string {


    if (!commonTables?.Domains || !domainInnerName) return domainInnerName
    const newName = domainInnerName.split('.').join('__')
    const found = commonTables.Domains.find((d: any) => d.InnerName === newName)
    return found?.DisplayName || domainInnerName
}

interface FlatRow {
    node: any
    uniqueId: string
    depth: number
    hasChildren: boolean
}

function flattenTree(node: any, path: string, depth: number, expandedSet: Set<string>, out: FlatRow[]): void {
    if (!node) return
    const nodeId = String(node.id)
    const uniqueId = `${path}/${nodeId}`
    const hasChildren = node.children && node.children.length > 0
    out.push({node, uniqueId, depth, hasChildren})
    if (hasChildren && expandedSet.has(uniqueId)) {
        node.children.forEach((child: any, index: number) => {
            flattenTree(child, `${uniqueId}-${index}`, depth + 1, expandedSet, out)
        })
    }
}

function getNodeLabel(node: any, formulas: any, commonTables: any): string {
    if (node.id === 'root' && node.name === 'Execution Tree') {
        return `Execution Tree (${node.children?.length ?? 0} roots)`
    }
    if (node.type === 'label') {
        return node.name || 'Label'
    }
    const nodeId = String(node.id)
    const result = findFormulaById(formulas, node.id)
    const coverageDisplay = result?.coverage ? getCoverageDescription(result.coverage, commonTables) : ''
    const domainDisplay = result?.domain ? getDomainDisplayName(result.domain, commonTables) : ''
    let label = ''
    label += result?.formula?.isBCR?`[ISO-${nodeId}]`:`[${nodeId}]`
    label += result?.domain?` → ${domainDisplay}`:``
    label += result?.coverage === 'NoCoverage'?'' :` → ${coverageDisplay}`
    label += ` → ${result?.formula?.FieldName}`
    label += ` → [${node.value}]`
    return label
}

function ExecutionTreeTab({record}: ExecutionTreeTabProps) {
    const [executionTreeData, setExecutionTreeData] = useState<any>(null)
    const [executionTreeLoading, setExecutionTreeLoading] = useState(false)
    const [executionTreeError, setExecutionTreeError] = useState<string | null>(null)

    const [formulasData, setFormulasData] = useState<any>(null)
    const [formulasLoading, setFormulasLoading] = useState(false)
    const [formulasError, setFormulasError] = useState<string | null>(null)

    const [commonTablesData, setCommonTablesData] = useState<any>(null)
    const [commonTablesLoading, setCommonTablesLoading] = useState(false)
    const [commonTablesError, setCommonTablesError] = useState<string | null>(null)

    const commonPath = record?.data?.originalEvent?.meta?.commonPath
    const executionTree = record?.data?.responseS3?.executionTree

    useEffect(() => {
        if (executionTree?.bucketName && executionTree?.key) {
            setExecutionTreeLoading(true)
            setExecutionTreeError(null)
            setFormulasLoading(true)
            setFormulasError(null)
            fetchS3File(executionTree)
                .then(result => {
                    setExecutionTreeData(result.executionTree);
                    setFormulasData(result.formulas)
                })
                .catch((err: any) => setExecutionTreeError(err.message))
                .finally(() => {
                    setExecutionTreeLoading(false);
                    setFormulasLoading(false)
                })
        }

        if (commonPath?.bucketName && commonPath?.key) {
            setCommonTablesLoading(true)
            setCommonTablesError(null)
            fetchS3File({bucketName: commonPath.bucketName, key: commonPath.key + '/tables.json'})
                .then(result => setCommonTablesData(result))
                .catch((err: any) => setCommonTablesError(err.message))
                .finally(() => setCommonTablesLoading(false))
        }
    }, [executionTree?.bucketName, executionTree?.key, commonPath?.bucketName, commonPath?.key])


    const [selectedNode, setSelectedNode] = useState<any>(null)
    const [formulaTabIndex, setFormulaTabIndex] = useState(0)
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
    const [searchInput, setSearchInput] = useState('')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchIndex, setSearchIndex] = useState(0)
    const [expandedItems, setExpandedItems] = useState<string[]>([])
    const [expandedCallRows, setExpandedCallRows] = useState<Record<number, boolean>>({})
    const [highlightCalls, setHighlightCalls] = useState(false)
    const treeContainerRef = useRef<HTMLDivElement>(null)
    const [sidebarWidth, setSidebarWidth] = useState(0.5)
    const isDragging = useRef(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const expressionEditorRef = useRef<any | null>(null)
    const monacoRef = useRef<any | null>(null)
    const expressionDecorationsRef = useRef<string[] | null>(null)

    const isLoading = executionTreeLoading || formulasLoading || commonTablesLoading
    const rootNode = useMemo(() => {
        if (!executionTreeData) return null
        if (Array.isArray(executionTreeData) && executionTreeData.length > 1) {
            return {id: 'root', name: 'Execution Tree', value: '', children: executionTreeData}
        }
        return executionTreeData?.[0] || executionTreeData
    }, [executionTreeData])

    const nodeMap = useMemo(() => {
        const map = new Map<string, any>()
        if (rootNode) {
            buildNodeMap(rootNode, 'root', map)
        }
        return map
    }, [rootNode])

    const expandedSet = useMemo(() => new Set(expandedItems), [expandedItems])

    const flattenedRows = useMemo(() => {
        if (!rootNode) return []
        const rows: FlatRow[] = []
        flattenTree(rootNode, 'root', 0, expandedSet, rows)
        return rows
    }, [rootNode, expandedSet])

    const toggleExpand = useCallback((uniqueId: string) => {
        setExpandedItems(prev => {
            const set = new Set(prev)
            if (set.has(uniqueId)) {
                set.delete(uniqueId)
            } else {
                set.add(uniqueId)
            }
            return Array.from(set)
        })
    }, [])

    const virtuosoRef = useRef<any>(null)
    const pendingScrollId = useRef<string | null>(null)

    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return []
        const query = searchQuery.trim()
        const results: string[] = []
        nodeMap.forEach((node, itemId) => {
            const nodeId = String(node.id)
            const formulaResult = findFormulaById(formulasData, node.id)
            const formulaId = formulaResult?.formula?.RatingFormulaColumnID
            const fieldName = formulaResult?.formula?.FieldName
            if (nodeId === query || (formulaId && String(formulaId) === query) || (fieldName && String(fieldName).toLowerCase().includes(query.toLowerCase()))) {
                results.push(itemId)
            }
        })
        return results
    }, [searchQuery, nodeMap, formulasData])

    const handleSelectNode = useCallback((node: any) => {
        setSelectedNode(node)
        const result = findFormulaById(formulasData, node.id)
        const columnType = result?.formula?.ColumnType
        setFormulaTabIndex((columnType === 'E' || columnType === 'Q' || columnType === 'M') ? 1 : 0)
    }, [formulasData])

    const navigateToResult = useCallback((index: number) => {
        if (searchResults.length === 0) return
        const wrappedIndex = ((index % searchResults.length) + searchResults.length) % searchResults.length
        setSearchIndex(wrappedIndex)
        const itemId = searchResults[wrappedIndex]
        if (itemId && nodeMap.has(itemId)) {
            const expandPaths: string[] = []
            const allItemIds = Array.from(nodeMap.keys())
            for (const id of allItemIds) {
                if (itemId.startsWith(id) && id !== itemId) {
                    expandPaths.push(id)
                }
            }
            setExpandedItems(prev => [...new Set([...prev, ...expandPaths])])
            setSelectedItemId(itemId)
            handleSelectNode(nodeMap.get(itemId))
            pendingScrollId.current = itemId
        }
    }, [searchResults, nodeMap, handleSelectNode])

    const handleSearch = useCallback(() => {
        if (searchInput === searchQuery && searchResults.length > 0) {
            navigateToResult(searchIndex + 1)
        } else {
            setSearchQuery(searchInput)
            setSearchIndex(0)
        }
    }, [searchInput, searchQuery, searchResults, searchIndex, navigateToResult])

    useEffect(() => {
        if (searchResults.length > 0) {
            navigateToResult(0)
        }
    }, [searchResults])

    useEffect(() => {
        if (!pendingScrollId.current) return
        const rowIndex = flattenedRows.findIndex(r => r.uniqueId === pendingScrollId.current)
        if (rowIndex >= 0 && virtuosoRef.current) {
            virtuosoRef.current.scrollToIndex({index: rowIndex, align: 'center', behavior: 'smooth'})
            pendingScrollId.current = null
        }
    }, [flattenedRows])

    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (e.shiftKey && searchInput === searchQuery && searchResults.length > 0) {
                navigateToResult(searchIndex - 1)
            } else {
                handleSearch()
            }
        }
    }, [navigateToResult, searchIndex, searchQuery, searchInput, searchResults, handleSearch])

    // When a new node is selected, turn off highlighting by default
    useEffect(() => {
        if (selectedNode) {
            setHighlightCalls(false)
        }
    }, [selectedNode?.id])

    // Highlight expression lines that correspond to selectedNode.calls
    useEffect(() => {
        const editor = expressionEditorRef.current
        const monaco = monacoRef.current
        if (!editor || !monaco || !monaco.Range) return

        // Clear decorations when toggle off or no calls
        if (!highlightCalls || !selectedNode?.calls || selectedNode.calls.length === 0) {
            if (expressionDecorationsRef.current) {
                expressionDecorationsRef.current = editor.deltaDecorations(expressionDecorationsRef.current, [])
            }
            return
        }

        const decorations = selectedNode.calls
            .filter((c: any) => typeof c.line === 'number' && c.line > 0)
            .map((c: any) => {
                const args = Array.isArray(c.args) ? c.args : []
                const formattedArgs = args
                    .map((arg: any) => (typeof arg === 'string' ? `"${arg}"` : JSON.stringify(arg)))
                    .join(', ')
                const value = c.results?.value
                const valueStr = value === undefined
                    ? '-'
                    : typeof value === 'string'
                        ? value
                        : JSON.stringify(value)

                const hoverText = [
                    `Line: ${c.line ?? '-'}, Column: ${c.column ?? '-'}`,
                    `Function: ${c.fn || '-'}(${formattedArgs})`,
                    `Value: ${valueStr}`
                ].join('  \n')

                return {
                    range: new monaco.Range(c.line, 1, c.line, 1000),
                    options: {
                        isWholeLine: true,
                        inlineClassName: 'execution-call-highlight-line',
                        hoverMessage: {value: hoverText}
                    }
                }
            })

        expressionDecorationsRef.current = editor.deltaDecorations(expressionDecorationsRef.current || [], decorations)
    }, [highlightCalls, selectedNode])

    return (
        <Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1}}>
                <Typography variant="subtitle2" sx={{fontWeight: 'bold'}}>Execution Tree</Typography>
                <TextField
                    size="small"
                    placeholder="Search by ID or Field Name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    sx={{width: 200}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        )
                    }}
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={handleSearch}
                    disabled={!searchInput.trim()}
                >
                    Search
                </Button>
                {searchResults.length > 0 && (
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                        <Typography variant="caption">
                            {searchIndex + 1}/{searchResults.length}
                        </Typography>
                        <IconButton size="small" onClick={() => navigateToResult(searchIndex - 1)}>
                            <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => navigateToResult(searchIndex + 1)}>
                            <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}
            </Box>
            {isLoading ? (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '12px'}}>
                    <CircularProgress size={20} />
                    <span>Loading...</span>
                </div>
            ) : executionTreeError ? (
                <div style={{color: 'red'}}>Error: {executionTreeError}</div>
            ) : rootNode ? (
                <Box
                    ref={containerRef}
                    sx={{display: 'flex', gap: 0, flexDirection: {xs: 'column', md: 'row'}, height: 'calc(90vh - 260px)'}}
                >
                    <Box ref={treeContainerRef} sx={{width: {md: `${sidebarWidth * 100}%`}, flex: {xs: 1, md: 'none'}, overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 1}}>
                        <Virtuoso
                            ref={virtuosoRef}
                            totalCount={flattenedRows.length}
                            overscan={200}
                            itemContent={(index) => {
                                const row = flattenedRows[index]
                                if (!row) return null
                                const {node, uniqueId, depth, hasChildren} = row
                                const isSelected = selectedItemId === uniqueId
                                const isExpanded = expandedSet.has(uniqueId)
                                const label = getNodeLabel(node, formulasData, commonTablesData)

                                return (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            pl: depth * 2.5 + 0.5,
                                            pr: 1,
                                            py: 0.25,
                                            cursor: 'pointer',
                                            backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                            '&:hover': {backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5'},
                                            borderBottom: '1px solid #f5f5f5',
                                            minHeight: 28
                                        }}
                                        onClick={() => {
                                            setSelectedItemId(uniqueId)
                                            handleSelectNode(node)
                                        }}
                                    >
                                        <Box
                                            sx={{width: 24, minWidth: 24, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                            onClick={(e) => {
                                                if (hasChildren) {
                                                    e.stopPropagation()
                                                    toggleExpand(uniqueId)
                                                }
                                            }}
                                        >
                                            {hasChildren && (
                                                isExpanded
                                                    ? <ExpandMoreIcon sx={{fontSize: 18, color: '#666'}}/>
                                                    : <ChevronRightIcon sx={{fontSize: 18, color: '#666'}}/>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: '0.8rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                fontWeight: isSelected ? 600 : 400
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Box>
                                )
                            }}
                        />
                    </Box>
                    <Box
                        onMouseDown={(e) => {
                            e.preventDefault()
                            isDragging.current = true
                            document.body.style.userSelect = 'none'
                            document.body.style.cursor = 'col-resize'
                            const onMouseMove = (ev: MouseEvent) => {
                                if (!isDragging.current || !containerRef.current) return
                                const rect = containerRef.current.getBoundingClientRect()
                                const ratio = (ev.clientX - rect.left) / rect.width
                                setSidebarWidth(Math.max(0.15, Math.min(0.85, ratio)))
                            }
                            const onMouseUp = () => {
                                isDragging.current = false
                                document.body.style.userSelect = ''
                                document.body.style.cursor = ''
                                document.removeEventListener('mousemove', onMouseMove)
                                document.removeEventListener('mouseup', onMouseUp)
                            }
                            document.addEventListener('mousemove', onMouseMove)
                            document.addEventListener('mouseup', onMouseUp)
                        }}
                        sx={{width: 6, minWidth: 6, cursor: 'col-resize', display: {xs: 'none', md: 'flex'}, alignItems: 'center', justifyContent: 'center', backgroundColor: '#e0e0e0', '&:hover': {backgroundColor: '#90caf9'}, borderRadius: 1, mx: 0.5}}
                    />
                    {selectedNode && (
                        <Box sx={{flex: 1, minWidth: 0, border: '1px solid #e0e0e0', borderRadius: 1, background: '#f5f5f5', overflow: 'auto', p: 2}}>

                            {(() => {
                                const result = findFormulaById(formulasData, selectedNode.id)
                                if (!result) return null
                                const isExpression = result.formula?.ColumnType === 'E'
                                const isBasicMath = result.formula?.ColumnType === 'Q'
                                const isModelColumn = result.formula?.ColumnType === 'M'
                                const hasCurrentDomain = !!selectedNode.currentDomain
                                const detailsTabIndex = (isExpression || isBasicMath || isModelColumn)
                                    ? 3
                                    : hasCurrentDomain
                                        ? 2
                                        : 1

                                return (
                                    <Box sx={{mt: 2, pt: 2 }}>
                                        <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                                            Formula: {result.coverage === 'NoCoverage' ? getDomainDisplayName(result.domain, commonTablesData) : `${getDomainDisplayName(result.domain, commonTablesData)} → ${getCoverageDescription(result.coverage, commonTablesData)}`}  → {result.formula?.FieldName}
                                        </Typography>
                                        <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                                            <Tabs
                                                value={formulaTabIndex}
                                                onChange={(_, v) => setFormulaTabIndex(v)}
                                                sx={{minHeight: 36}}
                                            >
                                                <Tab label="Rating Formula Column" sx={{minHeight: 36, py: 0}} />
                                                {isExpression && <Tab label="Expression" sx={{minHeight: 36, py: 0}} />}
                                                {isBasicMath && <Tab label="Basic Math Formula" sx={{minHeight: 36, py: 0}} />}
                                                {isModelColumn && <Tab label="Map From Source" sx={{minHeight: 36, py: 0}} />}
                                                {hasCurrentDomain && <Tab label="Current Domain" sx={{minHeight: 36, py: 0}} />}
                                                {selectedNode.calls &&<Tab label="Function calls" sx={{minHeight: 36, py: 0}}></Tab>}
                                            </Tabs>
                                        </Box>
                                        <Box sx={{pt: 1}}>
                                            {formulaTabIndex === 0 && (
                                                <JsonView data={result.formula} placeholder="No formula" />
                                            )}
                                            {formulaTabIndex === 1 && isExpression && (
                                                <Box>
                                                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1}}>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    size="small"
                                                                    checked={highlightCalls}
                                                                    onChange={(e) => setHighlightCalls(e.target.checked)}
                                                                />
                                                            }
                                                            label="Highlight calls"
                                                        />
                                                    </Box>
                                                    <Editor
                                                        height="calc(90vh - 390px)"
                                                        language="javascript"
                                                        theme="vs-dark"
                                                        value={result.formula.Expression}
                                                        options={{
                                                            readOnly: true,
                                                            minimap: {enabled: false},
                                                            scrollBeyondLastLine: true,
                                                            fontSize: 13,
                                                            wordWrap: 'on',
                                                            lineNumbers: 'on',
                                                            folding: true,
                                                            automaticLayout: true,
                                                            hover: {
                                                                above: false
                                                            }
                                                        }}
                                                        onMount={(editor, monaco) => {
                                                            expressionEditorRef.current = editor
                                                            monacoRef.current = monaco
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            {formulaTabIndex === 1 && isModelColumn && (() => {
                                                const f = result.formula
                                                const sourceDomain = f.SourceDomain
                                                const sourceFilter = f.SourceFilter
                                                const source = f.Source
                                                const aggregateType = f.AggregateType
                                                let translated = ''
                                                const singleRecordDomains = ['Policy', 'Proposal', 'Member']
                                                if (singleRecordDomains.includes(sourceDomain)) {
                                                    translated = `getModelColumn('${sourceDomain}', '${source}')`
                                                } else {
                                                    switch (aggregateType) {
                                                        case 'C': translated = `listCount('${sourceDomain}', '${sourceFilter ?? ''}')`; break
                                                        case 'S': translated = `listSum('${sourceDomain}', '${source}', '${sourceFilter ?? ''}')`; break
                                                        case 'A': translated = `listAverage('${sourceDomain}', '${source}', '${sourceFilter ?? ''}')`; break
                                                        case 'N': translated = `listMin('${sourceDomain}', '${source}', '${sourceFilter ?? ''}')`; break
                                                        case 'X': translated = `listMax('${sourceDomain}', '${source}', '${sourceFilter ?? ''}')`; break
                                                        default: translated = `/* unknown aggregate type: ${aggregateType} */`; break
                                                    }
                                                }
                                                const code = `return ${translated}`
                                                return (
                                                    <Editor
                                                        height="calc(90vh - 390px)"
                                                        language="javascript"
                                                        theme="vs-dark"
                                                        value={code}
                                                        options={{
                                                            readOnly: true,
                                                            minimap: {enabled: false},
                                                            scrollBeyondLastLine: false,
                                                            fontSize: 13,
                                                            wordWrap: 'on',
                                                            lineNumbers: 'on',
                                                            folding: true,
                                                            automaticLayout: true
                                                        }}
                                                    />
                                                )
                                            })()}
                                            {formulaTabIndex === 1 && isBasicMath && (
                                                <Box sx={{
                                                    p: 2,
                                                    background: '#1e1e1e',
                                                    borderRadius: 1,
                                                    fontFamily: 'monospace',
                                                    fontSize: '14px',
                                                    lineHeight: 1.8
                                                }}>
                                                    {result.formula.BasicMathFormula?.split(/(\{[^}]+\})/).map((part: string, i: number) => (
                                                        part.startsWith('{') && part.endsWith('}') ? (
                                                            <span key={i} style={{
                                                                color: '#9cdcfe',
                                                                background: '#2d2d2d',
                                                                padding: '2px 6px',
                                                                borderRadius: '3px',
                                                                margin: '0 2px'
                                                            }}>
                                                                {part}
                                                            </span>
                                                        ) : (
                                                            <span key={i} style={{color: '#d4d4d4'}}>{part}</span>
                                                        )
                                                    ))}
                                                </Box>
                                            )}
                                            {((isExpression || isBasicMath || isModelColumn) ? formulaTabIndex === 2 : formulaTabIndex === 1) && hasCurrentDomain && (
                                                <JsonView data={selectedNode.currentDomain} placeholder="No current domain data" />
                                            )}
                                            {formulaTabIndex === detailsTabIndex && (
                                                <Box sx={{mt: 1}}>
                                                    {!selectedNode.calls || selectedNode.calls.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{fontStyle: 'italic', color: '#666'}}
                                                        >
                                                            No call trace data available for this node.
                                                        </Typography>
                                                    ) : (
                                                        <Box sx={{fontSize: 12}}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    py: 0.5,
                                                                    borderBottom: '1px solid #ddd',
                                                                    fontWeight: 600,
                                                                    color: '#555'
                                                                }}
                                                            >
                                                                <Box sx={{width: 70,}}>Line</Box>
                                                                <Box sx={{width: 80}}>Column</Box>
                                                                <Box sx={{width: 120}}>Value</Box>
                                                                <Box sx={{minWidth:100}}>Function</Box>
                                                            </Box>
                                                            {selectedNode.calls.map((call: any, index: number) => {
                                                                const value = call?.results?.value
                                                                const isObject = value !== null && typeof value === 'object'
                                                                const isExpanded = !!expandedCallRows[index]

                                                                return (
                                                                    <>
                                                                        <Box
                                                                            key={`row-${index}`}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                py: 0.5,
                                                                                borderBottom: '1px dashed #eee',
                                                                                alignItems: 'flex-start'
                                                                            }}
                                                                        >
                                                                            <Box sx={{width: 70}}>
                                                                                {call.line ?? '-'}
                                                                            </Box>
                                                                            <Box sx={{width: 80}}>
                                                                                {call.column ?? '-'}
                                                                            </Box>
                                                                            <Box sx={{width:120 , fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                                                                                {isObject ? (
                                                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                                                                        <span>[object]</span>
                                                                                        <IconButton
                                                                                            size="small"
                                                                                            onClick={() => {
                                                                                                setExpandedCallRows(prev => ({
                                                                                                    ...prev,
                                                                                                    [index]: !prev[index]
                                                                                                }))
                                                                                            }}
                                                                                            sx={{p: 0.25}}
                                                                                        >
                                                                                            {isExpanded ? (
                                                                                                <KeyboardArrowUpIcon fontSize="inherit" />
                                                                                            ) : (
                                                                                                <KeyboardArrowDownIcon fontSize="inherit" />
                                                                                            )}
                                                                                        </IconButton>
                                                                                    </Box>
                                                                                ) : (
                                                                                    value === undefined ? '-' : String(value)
                                                                                )}
                                                                            </Box>

                                                                            <Box sx={{minWidth: 100, fontFamily: 'monospace'}}>
                                                                                {(() => {
                                                                                    const rawArgs = Array.isArray(call.args) ? call.args : []
                                                                                    const formatted = rawArgs.map((arg:any) => {
                                                                                        if (typeof arg === 'string') {
                                                                                            return `"${arg}"`
                                                                                        }
                                                                                        return JSON.stringify(arg)
                                                                                    }).join(', ')
                                                                                    return `${call.fn}(${formatted})`
                                                                                })()}
                                                                            </Box>


                                                                        </Box>
                                                                        {isObject && isExpanded && (
                                                                            <Box
                                                                                key={`details-${index}`}
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    pl: 1,
                                                                                    py: 0.5,
                                                                                    borderBottom: '1px dashed #eee',
                                                                                    backgroundColor: '#fafafa'
                                                                                }}
                                                                            >
                                                                                <Box sx={{flex: 1}}>
                                                                                    <JsonView data={value} placeholder="No value" />
                                                                                </Box>
                                                                            </Box>
                                                                        )}
                                                                    </>
                                                                )
                                                            })}
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                )
                            })()}
                        </Box>
                    )}
                </Box>
            ) : (
                <div style={{color: '#888', fontStyle: 'italic'}}>No execution tree data</div>
            )}

            {formulasError && (
                <Typography variant="caption" color="error" sx={{display: 'block', mt: 1}}>
                    Warning: Could not load formulas - {formulasError}
                </Typography>
            )}
            {commonTablesError && (
                <Typography variant="caption" color="error" sx={{display: 'block', mt: 1}}>
                    Warning: Could not load common tables - {commonTablesError}
                </Typography>
            )}
        </Box>
    )
}

export default ExecutionTreeTab
